import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Job } from '@/lib/types/job';
import { format } from 'date-fns';
import { Search } from 'lucide-react';

interface AddJobsToProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  orphanedJobs: Job[];
  onAddJobs: (jobIds: string[]) => Promise<void>;
}

export function AddJobsToProjectDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  orphanedJobs,
  onAddJobs,
}: AddJobsToProjectDialogProps) {
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedJobIds(new Set());
      setSearchQuery('');
    }
  }, [open]);

  const filteredJobs = orphanedJobs.filter((job) => {
    const query = searchQuery.toLowerCase();
    return (
      job.title.toLowerCase().includes(query) ||
      job.public_id.toLowerCase().includes(query) ||
      job.category?.toLowerCase().includes(query)
    );
  });

  const toggleJob = (jobId: string) => {
    const newSelected = new Set(selectedJobIds);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobIds(newSelected);
  };

  const handleAdd = async () => {
    if (selectedJobIds.size === 0) return;

    setAdding(true);
    try {
      await onAddJobs(Array.from(selectedJobIds));
      onOpenChange(false);
    } catch (error) {
      console.error('Error adding jobs:', error);
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Jobs to {projectName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search jobs by name, ID, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex-1 overflow-y-auto border rounded-md">
            {filteredJobs.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {orphanedJobs.length === 0
                  ? 'No jobs available to add. All jobs are already assigned to projects.'
                  : 'No jobs match your search.'}
              </div>
            ) : (
              <div className="divide-y">
                {filteredJobs.map((job) => (
                  <div
                    key={job.id}
                    className="p-4 hover:bg-accent/50 cursor-pointer flex items-start gap-3"
                    onClick={() => toggleJob(job.id)}
                  >
                    <Checkbox
                      checked={selectedJobIds.has(job.id)}
                      onCheckedChange={() => toggleJob(job.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium truncate">{job.title}</span>
                        <Badge variant="outline" className="text-xs">
                          {job.status.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <span className="font-mono">{job.public_id}</span>
                        {job.category && <span className="ml-2">• {job.category}</span>}
                        {job.due_date && (
                          <span className="ml-2">
                            • Due: {format(new Date(job.due_date), 'MMM d, yyyy')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedJobIds.size > 0 && (
            <div className="text-sm text-muted-foreground">
              {selectedJobIds.size} job{selectedJobIds.size !== 1 ? 's' : ''} selected
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={adding}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={selectedJobIds.size === 0 || adding}>
            {adding ? 'Adding...' : `Add ${selectedJobIds.size} Job${selectedJobIds.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
